import React, { useEffect, useMemo, useState } from 'react';
import apiClient from '../config/api';
import Navbar from '../components/Navbar';
import { styles as layoutStyles } from '../styles/newstyles';

const formatDbTime = (value) => {
  if (!value) return '--';
  try {
    // Display exactly what is stored (SQL returns Date interpreted as UTC)
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  } catch (err) {
    return value;
  }
};

const emptyForm = { domain: '', isAdminDefault: false, isActive: true };

const Tools = ({ onLogout, isAdmin, username }) => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [mode, setMode] = useState('add'); // 'add' | 'edit'
  const [showForm, setShowForm] = useState(false);

  const selectedDomain = useMemo(
    () => domains.find((d) => d.id === selectedId) || null,
    [domains, selectedId]
  );

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/tools/approved-email-domains');
      setDomains(res.data || []);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to load domains.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setMode('add');
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = () => {
    if (!selectedDomain) return;
    setMode('edit');
    setForm({
      domain: selectedDomain.Domain || selectedDomain.domain || '',
      isAdminDefault: !!selectedDomain.IsAdminDefault,
      isActive: !!selectedDomain.IsActive,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (mode === 'add') {
        await apiClient.post('/tools/approved-email-domains', {
          domain: form.domain,
          isAdminDefault: form.isAdminDefault,
          isActive: form.isActive,
        });
      } else if (mode === 'edit' && selectedDomain) {
        await apiClient.put(`/tools/approved-email-domains/${selectedDomain.id}`, {
          domain: form.domain,
          isAdminDefault: form.isAdminDefault,
          isActive: form.isActive,
        });
      }
      setShowForm(false);
      setForm(emptyForm);
      await load();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };


  const deactivate = async (row) => {
    if (!row) return;
    setSaving(true);
    setError(null);
    try {
      await apiClient.patch(`/tools/approved-email-domains/${row.id}/active`, {
        isActive: false,
      });
      if (row.id === selectedId) setSelectedId(null);
      await load();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to update active state.');
    } finally {
      setSaving(false);
    }
  };

  const renderForm = () => {
    if (!showForm) return null;
    return (
      <form onSubmit={handleSubmit} style={formStyles.wrapper}>
        <div style={formStyles.row}>
          <label style={formStyles.label}>Domain</label>
          <input
            type="text"
            value={form.domain}
            onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
            style={formStyles.input}
            placeholder="example.com"
            required
          />
        </div>
        <div style={formStyles.row}>
          <label style={formStyles.label}>Admin Default</label>
          <input
            type="checkbox"
            checked={form.isAdminDefault}
            onChange={(e) => setForm((f) => ({ ...f, isAdminDefault: e.target.checked }))}
          />
        </div>
        <div style={formStyles.row}>
          <label style={formStyles.label}>Active</label>
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
          />
        </div>
        <div style={formStyles.actions}>
          <button type="submit" disabled={saving} style={formStyles.primary}>
            {saving ? 'Saving...' : mode === 'add' ? 'Add Domain' : 'Update Domain'}
          </button>
          <button type="button" onClick={() => setShowForm(false)} style={formStyles.secondary}>
            Cancel
          </button>
        </div>
      </form>
    );
  };

  return (
    <div style={layoutStyles.mainContainer}>
      <Navbar username={username} onLogout={onLogout} isAdmin={isAdmin} />
      <div style={{ maxWidth: 1100, margin: '-40px auto 0', padding: '12px' }}>
        <div style={pageStyles.header}>
          <div>
            <h1 style={pageStyles.title}>Tools</h1>
            <p style={pageStyles.subtitle}>
              To add a domain: click “Add Domain”, enter the domain (no @), check “Admin Default” only if it should allow admin access, and keep “Active” checked so users can create accounts.
            </p>
          </div>
        </div>

        <div style={{ ...pageStyles.actions, marginTop: 8 }}>
          <button type="button" onClick={openAdd} style={pageStyles.primaryBtn}>
            Add Domain
          </button>
          <button
            type="button"
            onClick={openEdit}
            disabled={!selectedDomain}
            style={{
              ...pageStyles.secondaryBtn,
              opacity: selectedDomain ? 1 : 0.5,
              cursor: selectedDomain ? 'pointer' : 'not-allowed',
            }}
          >
            Edit (Select a row)
          </button>
        </div>

        {error && (
          <div style={pageStyles.error}>
            <button type="button" aria-label="Dismiss" onClick={() => setError(null)} style={pageStyles.errorClose}>
              ×
            </button>
            {error}
          </div>
        )}

        {renderForm()}

        {loading ? (
          <div style={{ padding: 16 }}>Loading...</div>
        ) : (
          <div style={{ marginTop: 16 }}>
            <div style={tableStyles.wrap}>
              <table style={tableStyles.table}>
                <thead>
                  <tr>
                    <th style={tableStyles.th}>Select</th>
                    <th style={tableStyles.th}>Domain</th>
                    <th style={tableStyles.th}>Admin Default</th>
                    <th style={tableStyles.th}>Active</th>
                    <th style={tableStyles.th}>Created (CST)</th>
                    <th style={tableStyles.th}>Updated (CST)</th>
                    <th style={tableStyles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {domains.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={tableStyles.empty}>No domains found.</td>
                    </tr>
                  ) : (
                    domains.map((row) => (
                      <tr key={row.id} style={row.IsActive ? undefined : tableStyles.inactiveRow}>
                        <td style={tableStyles.td}>
                          <input
                            type="radio"
                            name="selectedDomain"
                            checked={selectedId === row.id}
                            onChange={() => setSelectedId(row.id)}
                          />
                        </td>
                        <td style={tableStyles.td}>{row.Domain || row.domain}</td>
                        <td style={tableStyles.td}>{row.IsAdminDefault ? 'Yes' : 'No'}</td>
                        <td style={tableStyles.td}>{row.IsActive ? 'Active' : 'Inactive'}</td>
                        <td style={tableStyles.td}>{formatDbTime(row.CreatedAt)}</td>
                        <td style={tableStyles.td}>{formatDbTime(row.UpdatedAt)}</td>
                        <td style={tableStyles.td}>
                          {row.IsActive && (
                            <button
                              type="button"
                              onClick={() => deactivate(row)}
                              disabled={saving}
                              style={tableStyles.smallBtn}
                            >
                              Set Inactive
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const pageStyles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 },
  title: { margin: 0, color: '#023e8a' },
  subtitle: { margin: '4px 0 0', color: '#374151' },
  actions: { display: 'flex', gap: 10 },
  primaryBtn: {
    padding: '10px 14px',
    borderRadius: 10,
    border: '1px solid #1f6feb',
    background: 'linear-gradient(180deg,#1f6feb 0%,#0c4aad 100%)',
    color: '#fff',
    fontWeight: 700,
    cursor: 'pointer',
  },
  secondaryBtn: {
    padding: '10px 14px',
    borderRadius: 10,
    border: '1px solid #d1d5db',
    background: '#fff',
    color: '#111827',
    fontWeight: 600,
    cursor: 'pointer',
  },
  error: {
    marginTop: 12,
    padding: '10px 12px',
    borderRadius: 8,
    background: '#fef2f2',
    border: '1px solid #fecdd3',
    color: '#b91c1c',
    position: 'relative',
  },
  errorClose: {
    position: 'absolute',
    top: 8,
    right: 10,
    border: 'none',
    background: 'transparent',
    color: '#b91c1c',
    fontSize: 16,
    cursor: 'pointer',
    fontWeight: 700,
    lineHeight: 1,
  },
};

const formStyles = {
  wrapper: {
    marginTop: 14,
    padding: 14,
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    background: '#fff',
    boxShadow: '0 4px 10px rgba(0,0,0,0.04)',
  },
  row: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 },
  label: { width: 140, fontWeight: 600, color: '#111827' },
  input: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid #d1d5db',
    fontSize: 14,
  },
  actions: { display: 'flex', gap: 10, marginTop: 6 },
  primary: {
    padding: '10px 14px',
    borderRadius: 10,
    border: '1px solid #1f6feb',
    background: 'linear-gradient(180deg,#1f6feb 0%,#0c4aad 100%)',
    color: '#fff',
    fontWeight: 700,
    cursor: 'pointer',
  },
  secondary: {
    padding: '10px 14px',
    borderRadius: 10,
    border: '1px solid #d1d5db',
    background: '#fff',
    color: '#111827',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

const tableStyles = {
  wrap: { overflowX: 'auto', background: '#fff', borderRadius: 12, boxShadow: '0 6px 16px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 760 },
  th: { textAlign: 'left', padding: '12px 10px', fontSize: 13, color: '#374151', borderBottom: '1px solid #e5e7eb', background: '#f8fafc' },
  td: { padding: '11px 10px', fontSize: 13, color: '#111827', borderBottom: '1px solid #f1f5f9' },
  empty: { textAlign: 'center', padding: '14px 10px', color: '#6b7280' },
  inactiveRow: { background: '#f8fafc', color: '#6b7280' },
  smallBtn: {
    padding: '6px 10px',
    borderRadius: 8,
    border: '1px solid #f59e0b',
    background: '#fff7ed',
    color: '#b45309',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

export default Tools;

